import React from 'react';
import { Modal, Descriptions, Tag, Typography, Space, Button } from 'antd';
import { 
  CalendarOutlined, 
  UserOutlined, 
  BankOutlined, 
  PhoneOutlined,
  MailOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { ADMIN_REVIEW_MODAL_CLASS } from '../../features/dashboard/utils';
import './AdminAppointmentDetails.css';

const { Text } = Typography;

/**
 * AdminAppointmentDetails - Displays detailed information about an appointment
 */
const AdminAppointmentDetails = ({ visible, appointment, onClose, onConvert, onConfirm, canConfirmAppointment = false }) => {
  if (!appointment) {
    return null;
  }

  const getStatusTag = (status) => {
    const statusConfig = {
      scheduled: { color: 'blue', text: 'Scheduled' },
      rescheduled: { color: 'purple', text: 'Rescheduled' },
      confirmed: { color: 'cyan', text: 'Confirmed' },
      completed: { color: 'green', text: 'Completed' },
      cancelled: { color: 'red', text: 'Cancelled' },
      no_show: { color: 'orange', text: 'No Show' },
      expired: { color: 'volcano', text: 'Expired' },
    };
    const config = statusConfig[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const isConfirmableStatus = ['scheduled', 'rescheduled'].includes(String(appointment?.status || '').toLowerCase());

  return (
    <Modal
      rootClassName={ADMIN_REVIEW_MODAL_CLASS}
      className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog admin-appointment-details-modal`}
      title={
        <div className="appointment-detail-modal__title">
          <span>Appointment details</span>
          {getStatusTag(appointment.status)}
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        canConfirmAppointment && isConfirmableStatus && onConfirm && (
          <Button
            key="confirm"
            type="default"
            onClick={() => {
              onClose();
              onConfirm(appointment);
            }}
          >
            Confirm Appointment
          </Button>
        ),
        !appointment.converted_checkin_code && onConvert && (
          <Button
            key="convert"
            type="primary"
            onClick={() => {
              onClose();
              onConvert(appointment);
            }}
          >
            Convert to Check-in
          </Button>
        ),
      ].filter(Boolean)}
      width={700}
    >
      <div className="admin-appointment-details">
        <section className="appointment-detail-section">
          <div className="appointment-detail-section__head">
            <UserOutlined />
            Patient
          </div>
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Name">
              {appointment.patient_name || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Insurance Number">
              {appointment.insurance_no || 'N/A'}
            </Descriptions.Item>
            {appointment.first_name && (
              <Descriptions.Item label="First Name">
                {appointment.first_name}
              </Descriptions.Item>
            )}
            {appointment.middle_name && (
              <Descriptions.Item label="Middle Name">
                {appointment.middle_name}
              </Descriptions.Item>
            )}
            {appointment.surname && (
              <Descriptions.Item label="Surname">
                {appointment.surname}
              </Descriptions.Item>
            )}
            {appointment.phone && (
              <Descriptions.Item label="Phone">
                <Space>
                  <PhoneOutlined />
                  {appointment.phone}
                </Space>
              </Descriptions.Item>
            )}
            {appointment.email && (
              <Descriptions.Item label="Email">
                <Space>
                  <MailOutlined />
                  {appointment.email}
                </Space>
              </Descriptions.Item>
            )}
          </Descriptions>
        </section>

        <section className="appointment-detail-section">
          <div className="appointment-detail-section__head">
            <BankOutlined />
            Facility
          </div>
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Facility Name">
              {appointment.facility_name || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Facility ID">
              {appointment.facility_id || 'N/A'}
            </Descriptions.Item>
            {(appointment.facility_city || appointment.facility_state) && (
              <Descriptions.Item label="Location" span={2}>
                {[appointment.facility_city, appointment.facility_state].filter(Boolean).join(', ')}
              </Descriptions.Item>
            )}
          </Descriptions>
        </section>

        <section className="appointment-detail-section">
          <div className="appointment-detail-section__head">
            <CalendarOutlined />
            Schedule
          </div>
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Scheduled Start">
              {appointment.scheduled_start
                ? dayjs(appointment.scheduled_start).format('MMM D, YYYY h:mm A')
                : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Scheduled End">
              {appointment.scheduled_end
                ? dayjs(appointment.scheduled_end).format('MMM D, YYYY h:mm A')
                : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              {getStatusTag(appointment.status)}
            </Descriptions.Item>
          </Descriptions>
        </section>

        {(appointment.reason || appointment.notes || appointment.cancellation_reason) && (
          <section className="appointment-detail-section">
            <div className="appointment-detail-section__head">Notes</div>
            <Descriptions column={1} bordered size="small">
              {appointment.reason && (
                <Descriptions.Item label="Reason">
                  {appointment.reason}
                </Descriptions.Item>
              )}
              {appointment.notes && (
                <Descriptions.Item label="Notes">
                  {appointment.notes}
                </Descriptions.Item>
              )}
              {appointment.cancellation_reason && (
                <Descriptions.Item label="Cancellation Reason">
                  <Text type="danger">{appointment.cancellation_reason}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>
          </section>
        )}

        {appointment.converted_checkin_code && (
          <section className="appointment-detail-section">
            <div className="appointment-detail-section__head">
              <CheckCircleOutlined style={{ color: '#49B870' }} />
              Check-in conversion
            </div>
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="Check-in Code">
                <Tag color="green">{appointment.converted_checkin_code}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Converted At">
                {appointment.converted_at
                  ? dayjs(appointment.converted_at).format('MMM D, YYYY h:mm A')
                  : 'N/A'}
              </Descriptions.Item>
              {appointment.converted_by_user_id && (
                <Descriptions.Item label="Converted By User ID">
                  {appointment.converted_by_user_id}
                </Descriptions.Item>
              )}
              {appointment.converted_by_user_type && (
                <Descriptions.Item label="Converted By User Type">
                  <Tag>{appointment.converted_by_user_type}</Tag>
                </Descriptions.Item>
              )}
            </Descriptions>
          </section>
        )}
      </div>
    </Modal>
  );
};

export default AdminAppointmentDetails;

